import type { OrderStatus } from "../api/types";

/**
 * How an order's states are NAMED and DESCRIBED to an operator.
 *
 * WHAT THIS FILE MAY NOT CONTAIN
 * Any statement about which state may follow which. That graph is
 * `ALLOWED_TRANSITIONS` in `backend/app/services/order_lifecycle.py`, it has
 * exactly one owner, and the backend hands the answer for a specific order to
 * the Dashboard as `allowed_transitions` on the detail response. A copy here —
 * even a well-meaning one used only to sort buttons or to grey one out — would
 * be a second definition of the business rule, free to disagree with the one
 * that actually runs, and the disagreement would show up as a button that is
 * offered and then refused.
 *
 * So what is below is a lookup from a state to words and a CSS class, and
 * nothing indexes one state by another. `orders.test.tsx` asserts that a
 * transition this file has copy for is still not rendered unless the backend
 * named it.
 */

/** The operator-facing name of a state.
 *
 * Sentence case, and close to the backend's own vocabulary on purpose: an
 * administrator who reads "Awaiting pickup" here and `fulfilment_pending` in a
 * support thread has to translate between them.
 */
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  placed: "Placed",
  confirmed: "Confirmed",
  fulfilment_pending: "Fulfilment pending",
  fulfilled: "Fulfilled",
  cancelled: "Cancelled",
};

/** A one-line explanation of what a state means, for the detail header. */
export const ORDER_STATUS_MEANING: Record<OrderStatus, string> = {
  placed: "The customer has ordered. Nobody has accepted it yet.",
  confirmed: "The shop has accepted this order.",
  fulfilment_pending: "Being prepared for the customer.",
  fulfilled: "Handed over. This order is finished.",
  cancelled: "Cancelled. The stock it held has been returned.",
};

/** The badge class for a state.
 *
 * Named per state rather than mapped onto the product badges: an order is not a
 * product, and reusing `badge--published` for `fulfilled` would couple two
 * lifecycles that only happen to have a green state each.
 */
export function orderBadgeClass(status: OrderStatus | string): string {
  return `badge orderStatus orderStatus--${status}`;
}

/** What the button that moves an order INTO a state is called, and what the
 *  confirmation says.
 *
 *  Keyed by the TARGET state, which is what the backend's `allowed_transitions`
 *  is a list of. It says how an action is worded — never whether it is
 *  available.
 */
export interface TransitionCopy {
  button: string;
  title: string;
  confirm: string;
  /** Only cancellation. It returns stock and cannot be undone: `cancelled` is
   *  terminal in the backend graph, so there is no route back from it. */
  destructive?: boolean;
  body: string;
  /** The default text offered in the reason box, which the backend requires
   *  with every transition and records in the audit trail. */
  reasonPlaceholder: string;
}

export const TRANSITION_COPY: Record<OrderStatus, TransitionCopy> = {
  placed: {
    button: "Return to placed",
    title: "Return this order to placed?",
    confirm: "Return to placed",
    body: "The order goes back to waiting for the shop to accept it.",
    reasonPlaceholder: "Why is this going back?",
  },
  confirmed: {
    button: "Confirm order",
    title: "Confirm this order?",
    confirm: "Confirm order",
    body:
      "The shop accepts this order. Stock stays held for it and the customer " +
      "keeps their place.",
    reasonPlaceholder: "Why is this being confirmed?",
  },
  fulfilment_pending: {
    button: "Start fulfilment",
    title: "Start preparing this order?",
    confirm: "Start fulfilment",
    body: "The order moves to being prepared for the customer.",
    reasonPlaceholder: "Why is this being prepared now?",
  },
  fulfilled: {
    button: "Mark fulfilled",
    title: "Mark this order fulfilled?",
    confirm: "Mark fulfilled",
    body:
      "The customer has their order. This is final — a fulfilled order cannot " +
      "be moved again, and a mistake after it is corrected with a new order " +
      "rather than by rewinding this one.",
    reasonPlaceholder: "Who received it, and when?",
  },
  cancelled: {
    button: "Cancel order",
    title: "Cancel this whole order?",
    confirm: "Cancel order",
    destructive: true,
    body:
      "Every item on this order is cancelled and the exact quantities go back " +
      "into stock. This is final — a cancelled order cannot be reopened — and " +
      "the prices, lines and total are kept exactly as they were. Individual " +
      "items cannot be cancelled on their own.",
    reasonPlaceholder: "Why is the customer cancelling?",
  },
};

/** Payment method, in words. One value exists; it is spelled out rather than
 *  printed raw so an operator reads "Cash on delivery", not a column name. */
export function paymentMethodLabel(method: string): string {
  return method === "cash_on_delivery" ? "Cash on delivery" : method;
}
